import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock, MessageSquare, GitBranch, FileText, Plus, Trash2, AlertTriangle, ChevronRight } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { fmt } from "@/components/app/bits";
import { useAuth } from "@/context/AuthContext";

const CONF_LABEL = { asserted: "Asserted · one account", corroborated: "Corroborated · accounts agree", documented: "Documented · record backs it" };
const CONF_STYLE = { asserted: "bg-warning-100 text-warning-800", corroborated: "bg-info-100 text-info-900", documented: "bg-success-100 text-success-900" };

function daysLeft(due) {
  if (!due) return null;
  return Math.ceil((new Date(due + "T00:00:00") - new Date(new Date().toDateString())) / 86400000);
}

function StartAssess({ change, onSaved }) {
  const weeks3 = new Date(Date.now() + 21 * 86400000).toISOString().slice(0, 10);
  const [due, setDue] = useState(weeks3);
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setBusy(true);
    try { await api.post(`/changes/${change.id}/stage`, { stage: "assess", due }); toast.success("Assess diagnostic started"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const max = new Date(Date.now() + 56 * 86400000).toISOString().slice(0, 10);
  const min = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  return (
    <div className="pp-card p-6" data-testid="assess-start">
      <h3 className="font-head text-xl font-extrabold">Run an Assess diagnostic</h3>
      <p className="text-sm text-subtle mt-2 max-w-2xl">You've walked into a change already underway. Assess is the time-boxed diagnostic you run first: interview the people involved, reconstruct what happened, and reach a defensible view of what's recoverable — before committing to a plan. ChangeOS holds the people side; schedule, budget and technical assessment stay in your project tool.</p>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="assess-due" className="pp-label">Diagnostic ends on</label>
          <input id="assess-due" data-testid="assess-due-input" type="date" min={min} max={max} className="pp-input" value={due} onChange={(e) => setDue(e.target.value)} />
          <p className="text-[11px] text-muted-foreground mt-1">Defaults to three weeks, capped at eight. A diagnostic with no end date drifts into an open-ended engagement.</p>
        </div>
        <button data-testid="assess-start-btn" onClick={start} disabled={busy} className="pp-btn-dark">Start diagnostic <ChevronRight size={16} /></button>
      </div>
    </div>
  );
}

export default function AssessTab({ change, onSaved, onGo }) {
  const { can } = useAuth();
  const inAssess = change.stage === "assess";
  if (!inAssess) return <StartAssess change={change} onSaved={onSaved} />;
  return <Diagnostic change={change} onSaved={onSaved} canWrite={can("write")} onGo={onGo} />;
}

function Diagnostic({ change, onSaved, canWrite, onGo }) {
  const id = change.id;
  const [questions, setQuestions] = useState([]);
  const [byq, setByq] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [confLevels, setConfLevels] = useState(["asserted", "corroborated", "documented"]);
  const [note, setNote] = useState(change.assess_note || "");
  const [savingNote, setSavingNote] = useState(false);
  const left = daysLeft(change.stage_due);

  const load = useCallback(async () => {
    try {
      const [q, b, iv, tl] = await Promise.all([
        api.get(`/changes/${id}/interviews/questions`),
        api.get(`/changes/${id}/interviews/by-question`),
        api.get(`/changes/${id}/interviews`),
        api.get(`/changes/${id}/timeline`),
      ]);
      setQuestions(q.data.questions); setByq(b.data.groups); setInterviews(iv.data.items);
      setTimeline(tl.data.items); setConfLevels(tl.data.confidence_levels);
    } catch (e) { toast.error(errMsg(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const saveNote = async () => {
    setSavingNote(true);
    try { await api.patch(`/changes/${id}`, { assess_note: note }); toast.success("Assessment saved"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); } finally { setSavingNote(false); }
  };
  const genFindings = async () => {
    try {
      const r = await api.post(`/reports/change/${id}/findings`, {}, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = `${change.ref || "change"}-findings.pdf`; a.click(); URL.revokeObjectURL(url);
      toast.success("Findings report generated");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6" data-testid="assess-panel">
      <div className={`rounded-2xl border p-4 flex items-center justify-between flex-wrap gap-3 ${left != null && left <= 5 ? "border-warning-200 bg-warning-50" : "border-border bg-secondary"}`} data-testid="assess-countdown">
        <div className="flex items-center gap-3">
          <Clock size={18} className={left != null && left <= 5 ? "text-warning-700" : "text-muted-foreground"} aria-hidden="true" />
          <div>
            <div className="font-bold text-foreground">{left == null ? "Assess in progress" : left < 0 ? `Assess window closed ${-left} day${left === -1 ? "" : "s"} ago` : `${left} day${left === 1 ? "" : "s"} left in the diagnostic`}</div>
            <div className="text-xs text-muted-foreground">Ends {fmt(change.stage_due)} · the diagnostic terminates in a findings report for the sponsor</div>
          </div>
        </div>
        <button data-testid="assess-findings-btn" onClick={genFindings} className="pp-btn-ghost"><FileText size={15} /> Generate findings report</button>
      </div>

      <PulseCallout change={change} />

      <div className="rounded-2xl border border-info-200 bg-info-50 p-4" data-testid="assess-comprehension-callout">
        <div className="font-bold text-info-900">Test what landed, not just what people feel</div>
        <p className="text-sm text-info-900/90 mt-1">After a failed attempt the gap between "we feel informed" and "we understood" is enormous — and they lead to opposite fixes. A comprehension check distinguishes a communication failure from an absorption failure in the previous attempt. {onGo ? <button type="button" data-testid="assess-comprehension-link" onClick={() => onGo("readiness")} className="font-bold underline">Open "Did it land?" in the Readiness check tab</button> : <span className="font-semibold">Open the Readiness check tab to run one.</span>}</p>
      </div>

      <section data-testid="assess-interviews">
        <h3 className="font-head text-lg font-extrabold flex items-center gap-2"><MessageSquare size={17} className="text-accent-600" /> Interviews <span className="text-sm font-semibold text-muted-foreground">({interviews.length})</span></h3>
        <p className="text-sm text-subtle mt-1">Structured one-to-one discovery. Visible only to the change manager and owner — never in exports, the sponsor digest, or the AI co-pilot.</p>
        {canWrite && <InterviewForm id={id} questions={questions} stakeholders={change.stakeholders_lite} onAdded={load} />}
        <ByQuestion byq={byq} canWrite={canWrite} id={id} onAdded={load} />
      </section>

      <Timeline id={id} items={timeline} confLevels={confLevels} interviews={interviews} canWrite={canWrite} onChanged={load} />

      <section className="pp-card p-6" data-testid="assess-note">
        <h3 className="font-head text-lg font-extrabold">What's recoverable, and on what conditions</h3>
        <p className="text-sm text-subtle mt-1">Your assessment, in your own words. This is the heart of the findings report — attributed to you, not to anyone you interviewed.</p>
        <textarea data-testid="assess-note-input" className="pp-input mt-3 min-h-[120px]" value={note} onChange={(e) => setNote(e.target.value)} disabled={!canWrite} placeholder="e.g. Recoverable if the sponsor re-commits publicly and scope is cut to the expenses module only. Not recoverable as currently framed." />
        {canWrite && <button data-testid="assess-note-save" onClick={saveNote} disabled={savingNote} className="pp-btn-dark mt-3">Save assessment</button>}
      </section>
    </div>
  );
}

function PulseCallout({ change }) {
  return (
    <div className="rounded-2xl border border-info-200 bg-info-50 p-4" data-testid="assess-pulse-callout">
      <div className="font-bold text-info-900">The anonymous pulse matters more than usual here</div>
      <p className="text-sm text-info-900/90 mt-1">You're new and not yet trusted, and everyone has a version of events that protects them. The anonymous pulse is the one channel where people can be honest with someone they don't know yet. Run it as a shared link — in a low-trust situation, per-person codes read as tracking however carefully you explain them. Open the <span className="font-semibold">Readiness check</span> tab to launch one.</p>
    </div>
  );
}

function InterviewForm({ id, questions, onAdded }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [when, setWhen] = useState("");
  const [responses, setResponses] = useState({});
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!name.trim()) { toast.error("Who did you speak to?"); return; }
    setBusy(true);
    try {
      await api.post(`/changes/${id}/interviews`, { subject_name: name, subject_role: role, occurred_on: when || null, responses });
      setName(""); setRole(""); setWhen(""); setResponses({}); setOpen(false); onAdded(); toast.success("Interview recorded");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!open) return <button data-testid="interview-add-open" onClick={() => setOpen(true)} className="pp-btn-ghost mt-3"><Plus size={15} /> Record an interview</button>;
  return (
    <div className="pp-card p-5 mt-3" data-testid="interview-form">
      <div className="grid sm:grid-cols-3 gap-3">
        <div><label htmlFor="iv-name" className="pp-label">Who did you speak to?</label><input id="iv-name" data-testid="interview-name" className="pp-input" value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div><label htmlFor="iv-role" className="pp-label">Their role</label><input id="iv-role" className="pp-input" value={role} onChange={(e) => setRole(e.target.value)} /></div>
        <div><label htmlFor="iv-when" className="pp-label">When</label><input id="iv-when" type="date" className="pp-input" value={when} onChange={(e) => setWhen(e.target.value)} /></div>
      </div>
      <div className="mt-3 space-y-3">
        {questions.map((q) => (
          <div key={q.key}>
            <label htmlFor={`iv-${q.key}`} className="pp-label">{q.name}</label>
            <textarea id={`iv-${q.key}`} data-testid={`interview-q-${q.key}`} className="pp-input min-h-[60px]" placeholder={q.prompt} value={responses[q.key] || ""} onChange={(e) => setResponses({ ...responses, [q.key]: e.target.value })} />
          </div>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <button data-testid="interview-save" onClick={submit} disabled={busy} className="pp-btn-dark">Save interview</button>
        <button onClick={() => setOpen(false)} className="pp-btn-ghost">Cancel</button>
      </div>
    </div>
  );
}

function ByQuestion({ byq, canWrite, id, onAdded }) {
  const toTimeline = async (g, a) => {
    try { await api.post(`/changes/${id}/timeline`, { what: a.answer.slice(0, 200), source: `${a.subject} (interview)`, confidence: "asserted", interview_id: a.interview_id, response_key: g.key }); toast.success("Added to timeline"); onAdded(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!byq.some((g) => g.answers.length)) return <p className="text-sm text-muted-foreground mt-4" data-testid="byq-empty">No interview responses yet. Record an interview to start building the picture.</p>;
  return (
    <div className="mt-5 space-y-4" data-testid="assess-by-question">
      <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Responses by question — where accounts part company is a finding, not noise</div>
      {byq.filter((g) => g.answers.length).map((g) => (
        <div key={g.key} className="pp-card p-4" data-testid={`byq-${g.key}`}>
          <div className="flex items-center gap-2 font-bold text-foreground">{g.name}{g.diverges && <span data-testid={`byq-diverge-${g.key}`} className="inline-flex items-center gap-1 rounded-full bg-warning-100 text-warning-800 px-2 py-0.5 text-[11px] font-bold"><AlertTriangle size={11} aria-hidden="true" /> accounts diverge</span>}</div>
          <div className="mt-2 space-y-2">
            {g.answers.map((a, i) => (
              <div key={i} className="flex items-start justify-between gap-3 text-sm border-l-2 border-border pl-3">
                <div><span className="font-semibold text-body">{a.subject}{a.role ? ` · ${a.role}` : ""}:</span> <span className="text-subtle">{a.answer}</span></div>
                {canWrite && <button data-testid={`byq-to-timeline-${g.key}-${i}`} onClick={() => toTimeline(g, a)} title="Create a timeline event from this, keeping the source" className="shrink-0 text-xs font-bold text-accent-700 hover:underline"><GitBranch size={12} className="inline" /> to timeline</button>}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Timeline({ id, items, confLevels, canWrite, onChanged }) {
  const [open, setOpen] = useState(false);
  const [what, setWhat] = useState("");
  const [when, setWhen] = useState("");
  const [conf, setConf] = useState("asserted");
  const add = async () => {
    if (!what.trim()) { toast.error("What happened?"); return; }
    try { await api.post(`/changes/${id}/timeline`, { what, occurred_on: when || null, confidence: conf }); setWhat(""); setWhen(""); setConf("asserted"); setOpen(false); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (tid) => { try { await api.delete(`/timeline/${tid}`); onChanged(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <section data-testid="assess-timeline">
      <div className="flex items-center justify-between">
        <h3 className="font-head text-lg font-extrabold flex items-center gap-2"><GitBranch size={17} className="text-accent-600" /> Reconstructed timeline</h3>
        {canWrite && !open && <button data-testid="timeline-add-open" onClick={() => setOpen(true)} className="pp-btn-ghost"><Plus size={15} /> Add event</button>}
      </div>
      <p className="text-sm text-subtle mt-1">A defensible account assembled from fragments. One account is asserted; independent accounts that agree are corroborated; a record makes it documented.</p>
      {open && (
        <div className="pp-card p-4 mt-3" data-testid="timeline-form">
          <div className="grid sm:grid-cols-[1fr_auto_auto] gap-3">
            <div><label htmlFor="tl-what" className="pp-label">What happened</label><input id="tl-what" data-testid="timeline-what" className="pp-input" value={what} onChange={(e) => setWhat(e.target.value)} /></div>
            <div><label htmlFor="tl-when" className="pp-label">When</label><input id="tl-when" type="date" className="pp-input" value={when} onChange={(e) => setWhen(e.target.value)} /></div>
            <div><label htmlFor="tl-conf" className="pp-label">Confidence</label><select id="tl-conf" data-testid="timeline-confidence" className="pp-input" value={conf} onChange={(e) => setConf(e.target.value)}>{confLevels.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
          </div>
          <div className="mt-3 flex gap-2"><button data-testid="timeline-save" onClick={add} className="pp-btn-dark">Add to timeline</button><button onClick={() => setOpen(false)} className="pp-btn-ghost">Cancel</button></div>
        </div>
      )}
      {items.length === 0 ? <p className="text-sm text-muted-foreground mt-3" data-testid="timeline-empty">No events yet.</p> : (
        <ol className="mt-4 border-l-2 border-border ml-2 space-y-4">
          {items.map((e) => (
            <li key={e.id} className="relative pl-5" data-testid={`timeline-event-${e.id}`}>
              <span className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-accent-600" aria-hidden="true" />
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-mono text-muted-foreground">{e.occurred_on ? fmt(e.occurred_on) : "undated"}</span>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${CONF_STYLE[e.confidence] || "bg-secondary"}`} title={CONF_LABEL[e.confidence]}>{e.confidence}</span>
                {e.interview_id && <span className="text-[11px] text-muted-foreground">from interview</span>}
                {canWrite && <button onClick={() => del(e.id)} aria-label="Delete event" className="ml-auto text-muted-foreground hover:text-danger-600"><Trash2 size={13} /></button>}
              </div>
              <div className="text-sm text-body mt-0.5">{e.what}</div>
              {e.source && <div className="text-xs text-muted-foreground">Source: {e.source}</div>}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
